// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Holding as InterfaceHolding } from '@daml.js/splice-api-token-holding-v1-1.0.0/lib/Splice/Api/Token/HoldingV1';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import { SelectInput } from '../../../components/Form/SelectInput';
import TextInput from '../../../components/Form/TextInput';
import useBurnAndAttest from '../../../hooks/mutations/bridge/withdrawals/useBurnAndAttest';
import useMutate from '../../../hooks/mutations/useMutate';
import useBridgeUserAgreement from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreement';
import TemplateContract from '../../../utils/TemplateContract';
import { getValidateAddressMessage } from '../../../utils/getValidateAddressMessage';
import { getValidateAmountMessage } from '../../../utils/getValidateAmountMessage';
import { ERR_REQ_BRIDGE, SUC_REQ_BRIDGE } from '../../../utils/strings';
import { useSnackbar } from 'notistack';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  holdings: TemplateContract<Holding>[];
};

export const destinationDomains = [
    {
      value: '0',
      display: 'Ethereum',
    },
  ];

export const BurnAndAttestDialog = ({ open, onClose, holdings }: DialogProps) => {
  const [destinationDomain, setDestinationDomain] = useState<string>('');
  const [destinationRecipient, setDestinationRecipient] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const bridgeUserAgreement = useBridgeUserAgreement();
  const burnAndAttest = useBurnAndAttest();
  const [burnAmountValidationMsg, setBurnAmountValidationMsg] = useState<string>('');
  const [addressValidationMsg, setAddressValidationMsg] = useState<string>('');
  const mutate = useMutate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const minBurnAmount = bridgeUserAgreement.data?.payload.minBurnValue || '0';
  const holdingCids = holdings.map((h) => h.contractId);
  const totalHoldingAmount = holdings.reduce((sum, holding) => {
    return sum + Number(holding.payload.amount);
  }, 0).toString();

  const { showBoundary } = useErrorBoundary<Error>();

  const resetState = useCallback(() => {
    setDestinationDomain('');
    setDestinationRecipient('');
    setAmount('');
    setReference('');
  }, [setDestinationDomain, setDestinationRecipient, setAmount, setReference]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);
  const getBurnValidationMsg = getValidateAmountMessage('Bridge', totalHoldingAmount, minBurnAmount);
  const getAddressValidationMsg = getValidateAddressMessage('Bridge', destinationDomain);
  const handleSubmit = useCallback(async () => {
    if (!bridgeUserAgreement.data) {
      showBoundary(new Error(ERR_REQ_BRIDGE, { cause: 'Bridge user agreement is not available.' }));
      return;
    }
    const payload = {
      bridgeUserAgreementCid: bridgeUserAgreement.data.contractId,
      destinationDomain,
      destinationRecipient,
      holdingCids: holdingCids as unknown as ContractId<InterfaceHolding>[],
      totalHoldingAmount,
      amount,
      reference,
    };
    const result = await mutate(burnAndAttest, payload, SUC_REQ_BRIDGE, ERR_REQ_BRIDGE);
    if (result) {
        const updateId = result[1];
        enqueueSnackbar(`Update ID: ${updateId}`, {
          variant: 'success',
          autoHideDuration: 10000,
          action: (key) => (
            <button
              onClick={async () => {
                await navigator.clipboard.writeText(updateId);
                enqueueSnackbar('Update ID copied to clipboard', { variant: 'success' });
                closeSnackbar(key);
              }}
              style={{
                cursor: 'pointer',
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                textDecoration: 'underline',
              }}
            >
              Copy
            </button>
          ),
        });
      }
    handleClose();
  }, [bridgeUserAgreement, amount, reference, handleClose, mutate, showBoundary]);

  const { isLoading } = bridgeUserAgreement;
  if (isLoading) return null;

  const disableSubmit =
    !!burnAmountValidationMsg.length ||
    !!addressValidationMsg.length ||
    !amount;
  return (
    <FormDialog
      open={open}
      title="Withdraw USDC"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Withdraw"
      isSubmitDisabled={disableSubmit}
    >
      <SelectInput
        label="Destination Chain"
        value={destinationDomain}
        setValue={setDestinationDomain}
        values={destinationDomains}
        required
      />
      <TextInput
        label="Destination Recipient"
        value={destinationRecipient}
        setValue={setDestinationRecipient}
        validationMessage={addressValidationMsg}
        setValidationMessage={setAddressValidationMsg}
        getValidationMessage={getAddressValidationMsg}
        required
      />
      <TextInput
        type="number"
        validationMessage={burnAmountValidationMsg}
        setValidationMessage={setBurnAmountValidationMsg}
        getValidationMessage={getBurnValidationMsg}
        label="Amount"
        value={amount}
        setValue={setAmount}
        required
      />
      <TextInput label="Reference" value={reference} setValue={setReference} />
    </FormDialog>
  );
};
